/**
 * "Held by ETFs" card — shown when looking up a STOCK (not an ETF).
 * Collapsible. Headline shows total tracked ETFs holding this stock + how much
 * AUM that adds up to. Rows are clickable to dive into that ETF.
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Star, ArrowUpRight } from "lucide-react";
import { getEtfsHoldingStock } from "@/lib/api";
import EtfTypeChip from "./EtfTypeChip";

const fmtAum = (n) => {
  if (!n || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(0)}M`;
  return n.toString();
};

export default function HeldByEtfsCard({ symbol, isEtf, onPickTicker }) {
  // Only relevant for stocks
  const skip = !symbol || isEtf;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (skip) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getEtfsHoldingStock(symbol)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [symbol, skip]);

  if (skip) return null;
  if (loading) {
    return (
      <div
        data-testid="held-by-etfs-card"
        className="border border-zinc-800 bg-[#121214] p-5 flex items-center gap-2 text-xs font-mono text-zinc-500"
      >
        <Loader2 size={12} className="animate-spin" /> Scanning ETF universe…
      </div>
    );
  }
  if (!data || data.shown === 0) {
    return (
      <div
        data-testid="held-by-etfs-card"
        className="border border-zinc-800 bg-[#121214] p-5"
      >
        <div className="text-label mb-1">Held by ETFs</div>
        <div className="text-xs text-zinc-500 font-mono">
          None of our tracked ETFs currently list {symbol} in their top holdings. We
          extend the corpus daily — check back soon.
        </div>
      </div>
    );
  }

  const rows = data.rows || [];
  const universeAum = data.total_etf_aum_covered || 0;
  const topPicks = rows.filter((r) => r.is_top_holding).length;

  return (
    <section
      data-testid="held-by-etfs-card"
      className="border border-zinc-800 bg-[#121214]"
    >
      <button
        data-testid="held-by-etfs-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 px-5 sm:px-7 py-4 sm:py-5 text-left hover:bg-zinc-900/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="text-label mb-1">Held by ETFs</div>
          <div className="text-sm normal-case tracking-normal text-zinc-200">
            <span className="font-mono font-bold text-zinc-100">{symbol}</span>
            {" is held by "}
            <span className="font-mono font-bold text-zinc-100">{data.total_matches}</span>
            {" of our tracked ETFs"}
            {data.total_matches > data.shown && ` (showing the ${data.shown} largest)`}
            {topPicks > 0 && (
              <>
                {" — "}
                <span className="font-mono text-[#d7b5c9]">{topPicks}</span>
                {" as their #1 holding"}
              </>
            )}
            {universeAum > 0 && (
              <>
                {" · "}
                <span className="font-mono text-[#d7b5c9]">${fmtAum(universeAum)}</span>
                {" combined AUM"}
              </>
            )}
            .
          </div>
        </div>
        <span className="text-zinc-500 mt-1 shrink-0">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open && (
        <ol
          data-testid="held-by-etfs-list"
          className="border-t border-zinc-900 divide-y divide-zinc-900"
        >
          {rows.map((r) => {
            const pct = (r.pct_of_etf || 0) * 100;
            return (
              <li key={r.symbol} className="px-5 sm:px-7 py-3">
                <button
                  data-testid={`held-by-etf-${r.symbol}`}
                  onClick={() => onPickTicker?.(r.symbol)}
                  className="w-full grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 sm:gap-4 text-left hover:opacity-90 transition-opacity normal-case tracking-normal"
                >
                  <span className="font-mono font-bold text-zinc-100 inline-flex items-center gap-1.5 min-w-[3.5rem]">
                    {r.symbol}
                    {r.is_top_holding && (
                      <Star
                        size={11}
                        className="fill-[#d7b5c9] text-[#d7b5c9]"
                        aria-label={`${symbol} is the top holding of ${r.symbol}`}
                      />
                    )}
                  </span>
                  <span className="min-w-0">
                    <div className="text-sm text-zinc-200 truncate">{r.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{r.family || r.category || "ETF"}</span>
                      {/* Non-clickable here — the row itself is already a button (no nested buttons) */}
                      {r.type && <EtfTypeChip type={r.type} size="sm" />}
                    </div>
                  </span>
                  <span className="text-right">
                    <div className="font-mono text-sm text-zinc-100 tabular-nums">{pct.toFixed(2)}%</div>
                    <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                      of ETF
                    </div>
                  </span>
                  <span className="text-right pl-2 border-l border-zinc-900 hidden sm:block">
                    <div className="font-mono text-sm text-zinc-100 tabular-nums">${fmtAum(r.aum)}</div>
                    <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                      Fund AUM
                    </div>
                  </span>
                  <ArrowUpRight size={14} className="text-zinc-500 hidden sm:inline" />
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
